import {Box, Show, Text, VStack} from 'platform/foundation';
import {cva} from 'platform/shared';

import {ChangeEvent, useId} from 'react';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {getDataAttribute} from '../../utils/getDataAttribute';
import {HelperText} from '../HelperText/HelperText';

export interface SwitchProps extends FormControlProps<boolean>, TestIdProps {
  'aria-label'?: string;
  isLoading?: boolean;
  // A unique ID for the input element, allowing it to be associated with a <label> element in another component.
  externalLabelId?: string;
}

const switchWrapperVariants = cva('relative flex cursor-pointer select-none', {
  variants: {
    isDisabled: {
      true: 'cursor-not-allowed',
      false: '',
    },
  },
});

const switchTrackVariants = cva(
  'inline-flex h-5 w-9 shrink-0 cursor-pointer justify-start rounded-[var(--radius-large)] bg-[var(--color-palettes-neutral-200-100)] p-[calc(var(--spacing-1)/2)] transition-all duration-150 ease-[ease] hover:[box-shadow:0_0_0_var(--spacing-1)_var(--color-palettes-blue-20-100)] active:[box-shadow:0_0_0_var(--spacing-1)_var(--color-palettes-blue-30-100)]',
  {
    variants: {
      isChecked: {
        true: 'bg-[var(--color-accent)]',
        false: '',
      },
      isDisabled: {
        true: 'cursor-not-allowed opacity-40',
        false: '',
      },
      isFocused: {
        true: 'outline-none ![box-shadow:0_0_0_var(--spacing-1)_var(--color-palettes-blue-40-100)]',
        false: '',
      },
    },
  }
);

const switchThumbVariants = cva(
  'h-4 w-4 rounded-[var(--radius-large)] bg-[var(--color-white)] shadow-[var(--shadow-elevation-1)] transition-[transform] duration-200 ease-[ease]',
  {
    variants: {
      isChecked: {
        true: '[transform:translateX(var(--spacing-4))]',
        false: '',
      },
    },
  }
);

export function Switch(props: SwitchProps) {
  const id = useId();
  const [isFocused, handleFocus, handleBlur] = useBoolean(false);

  const isInvalid = props.isInvalid || !!props.errorMessage;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (props.isDisabled || props.isLoading) {
      event.preventDefault();
      return;
    }

    props.onChange?.(!props.value);
  };

  const handleFocusEvent = (event: React.FocusEvent<HTMLInputElement>) => {
    handleFocus();
    props.onFocus?.(event);
  };

  const handleBlurEvent = (event: React.FocusEvent<HTMLInputElement>) => {
    handleBlur();
    props.onBlur?.(event);
  };

  const rootProps = {
    'data-disabled': getDataAttribute(props.isDisabled),
    'data-checked': getDataAttribute(props.value),
    'data-invalid': getDataAttribute(isInvalid),
  };

  const checkboxProps = {
    'data-checked': getDataAttribute(props.value),
    'data-disabled': getDataAttribute(props.isDisabled),
    'data-invalid': getDataAttribute(isInvalid),
    'data-focus': getDataAttribute(isFocused),
  };

  return (
    <VStack align="flex-start">
      <label
        htmlFor={props.externalLabelId ?? id}
        {...rootProps}
        data-testid={suffixTestId('switchWrapper', props)}
        className={switchWrapperVariants({isDisabled: Boolean(props.isDisabled)})}
      >
        <input
          id={props.externalLabelId ?? id}
          type="checkbox"
          role="switch"
          checked={props.value}
          onChange={handleChange}
          onFocus={handleFocusEvent}
          onBlur={handleBlurEvent}
          disabled={props.isDisabled || props.isLoading}
          required={props.isRequired}
          aria-checked={props.value}
          aria-busy={props.isLoading}
          aria-label={props['aria-label']}
          aria-disabled={props.isDisabled || props.isLoading}
          data-testid={suffixTestId('switchInput', props)}
          className="absolute m-0 h-0 w-0 overflow-hidden p-0 whitespace-nowrap [border:0] [clip:rect(0,_0,_0,_0)]"
        />
        <span
          {...checkboxProps}
          data-testid={suffixTestId('switchTrack', props)}
          className={switchTrackVariants({
            isChecked: props.value,
            isDisabled: Boolean(props.isDisabled),
            isFocused,
          })}
        >
          <span
            data-checked={getDataAttribute(props.value)}
            data-testid={suffixTestId('switchThumb', props)}
            className={switchThumbVariants({isChecked: props.value})}
          />
        </span>
        <Show when={props.label}>
          <Box paddingLeft={3}>
            <Show when={props.label}>
              <Text size="small" data-testid={suffixTestId('label', props)}>
                {props.label}
              </Text>
            </Show>
            <Show when={props.helperText}>
              <HelperText
                helperText={props.helperText}
                data-testid={suffixTestId('helper', props)}
              />
            </Show>
            <Show when={props.errorMessage}>
              <HelperText
                errorMessage={props.errorMessage}
                data-testid={suffixTestId('errorMessage', props)}
              />
            </Show>
          </Box>
        </Show>
      </label>
    </VStack>
  );
}
